/**
 * SearchSpring Sorting Module
 *
 * Renders sort dropdown matching Horizon's sorting.liquid structure.
 * Desktop: accordion with radio buttons. Mobile: <select> dropdown.
 *
 * Loaded as standalone script by ss-collection.liquid.
 * Depends on: window.SSURLState, window.__ssCollection
 */

(function () {
  'use strict';

  const CARET_SVG =
    '<span class="svg-wrapper icon-caret icon-animated">' +
    '<svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg></span>';

  const CHECKMARK_SVG =
    '<span class="svg-wrapper icon-checkmark sorting-filter__checkmark">' +
    '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg></span>';

  // Double-sided caret for mobile select
  const SELECT_CARET_SVG =
    '<svg aria-hidden="true" focusable="false" class="icon icon-caret" width="8" height="13" viewBox="0 0 8 13">' +
    '<path d="M4 1L7 4.5H1L4 1Z" fill="currentColor"/>' +
    '<path d="M4 12L1 8.5H7L4 12Z" fill="currentColor"/>' +
    '</svg>';

  // Athos returns HTML-encoded text; decode before re-escaping.
  function decodeEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = String(str);
    return txt.value;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return decodeEntities(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    if (!str) return '';
    return decodeEntities(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  /**
   * Map SS sort option to human-friendly label.
   */
  function getSortLabel(field, direction) {
    const key = field + '_' + direction;
    const labels = {
      relevance_asc: 'Featured',
      relevance_desc: 'Featured',
      price_asc: 'Price, low to high',
      price_desc: 'Price, high to low',
      ss_dateAdded_desc: 'Date, new to old',
      ss_dateAdded_asc: 'Date, old to new',
      name_asc: 'Alphabetically, A-Z',
      name_desc: 'Alphabetically, Z-A',
    };
    return labels[key] || field + ' (' + direction + ')';
  }

  /**
   * Build a sort option value string from field + direction.
   */
  function sortValue(option) {
    return (option.field || 'relevance') + '_' + (option.direction || 'asc');
  }

  /**
   * Get current sort from URL params.
   */
  function getCurrentSort() {
    const params = window.SSURLState.readFromURL();
    for (const [key, value] of Object.entries(params)) {
      if (key.startsWith('sort.')) {
        const field = key.replace('sort.', '');
        return { field, direction: value, value: field + '_' + value };
      }
    }
    return null;
  }

  class SSSorting {
    #containerEl;

    constructor() {
      // Event delegation will be bound when render is called
    }

    render(sortingOptions, containerEl) {
      this.#containerEl = containerEl;
      if (!this.#containerEl || !sortingOptions || !sortingOptions.length) return;

      const current = getCurrentSort();
      const currentValue = current ? current.value : sortValue(sortingOptions[0]);

      // Find the current label for status display
      let currentLabel = '';
      for (const opt of sortingOptions) {
        if (sortValue(opt) === currentValue) {
          currentLabel = opt.label || getSortLabel(opt.field, opt.direction);
          break;
        }
      }
      if (!currentLabel && sortingOptions.length > 0) {
        currentLabel = sortingOptions[0].label || getSortLabel(sortingOptions[0].field, sortingOptions[0].direction);
      }

      // Mobile select
      const options = sortingOptions
        .map((opt) => {
          const val = sortValue(opt);
          const label = opt.label || getSortLabel(opt.field, opt.direction);
          return (
            '<option value="' + escapeAttr(val) + '"' +
            (val === currentValue ? ' selected' : '') +
            '>' + escapeHtml(label) + '</option>'
          );
        })
        .join('');

      const mobileSelect =
        '<div class="sorting-filter__container desktop:hidden">' +
        '<label class="facets__label">Sort</label>' +
        '<div class="sorting-filter__select-wrapper">' +
        '<select class="sorting-filter__select ss-sort-select">' +
        options +
        '</select>' +
        SELECT_CARET_SVG +
        '</div></div>';

      // Desktop accordion with radio buttons
      const radioButtons = sortingOptions
        .map((opt) => {
          const val = sortValue(opt);
          const label = opt.label || getSortLabel(opt.field, opt.direction);
          const isChecked = val === currentValue;
          return (
            '<label for="ss-sort-' + escapeAttr(val) + '">' +
            '<div class="sorting-filter__option" role="option"' +
            ' aria-selected="' + isChecked + '"' +
            ' tabindex="' + (isChecked ? '0' : '-1') + '">' +
            '<input type="radio" name="ss_sort_by" id="ss-sort-' + escapeAttr(val) + '"' +
            ' value="' + escapeAttr(val) + '"' +
            ' class="sorting-filter__input ss-sort-radio"' +
            ' data-option-name="' + escapeAttr(label) + '"' +
            (isChecked ? ' checked' : '') +
            ' aria-hidden="true">' +
            CHECKMARK_SVG +
            '<span class="sorting-filter__label">' + escapeHtml(label) + '</span>' +
            '</div></label>'
          );
        })
        .join('');

      const desktopAccordion =
        '<accordion-custom data-disable-animation-on-desktop="true" data-close-with-escape="true">' +
        '<details class="facets__panel mobile:hidden" data-auto-close-details="desktop,mobile">' +
        '<summary class="facets__summary" role="button" aria-expanded="false">' +
        '<span class="facets__label">Sort</span>' +
        '<span class="facets__status h6 desktop:hidden">' + escapeHtml(currentLabel) + '</span>' +
        CARET_SVG +
        '</summary>' +
        '<floating-panel-component class="sorting-filter__options" role="listbox">' +
        '<div class="sorting-filter__options-inner">' +
        radioButtons +
        '</div></floating-panel-component>' +
        '</details></accordion-custom>';

      this.#containerEl.innerHTML =
        '<div class="sorting-filter sorting-filter__horizontal">' +
        mobileSelect +
        desktopAccordion +
        '</div>';

      // Bind events
      const select = this.#containerEl.querySelector('.ss-sort-select');
      if (select) {
        select.addEventListener('change', this.#handleSortChange.bind(this));
      }

      this.#containerEl.querySelectorAll('.ss-sort-radio').forEach((radio) => {
        radio.addEventListener('change', this.#handleSortChange.bind(this));
      });
    }

    #handleSortChange(e) {
      const value = e.target.value;
      const parts = value.split('_');
      const direction = parts.pop();
      const field = parts.join('_');

      // Sync mobile and desktop
      const select = this.#containerEl.querySelector('.ss-sort-select');
      if (select && select !== e.target) select.value = value;
      const radio = this.#containerEl.querySelector('.ss-sort-radio[value="' + CSS.escape(value) + '"]');
      if (radio && radio !== e.target) radio.checked = true;

      // Close the details panel on desktop after selection
      const details = this.#containerEl.querySelector('details.facets__panel');
      if (details) details.open = false;

      // Build params: keep filters, set new sort, reset to page 1
      const currentParams = window.SSURLState.readFromURL();
      const params = {};

      // Keep filter params
      for (const [key, val] of Object.entries(currentParams)) {
        if (key.startsWith('filter.')) {
          params[key] = val;
        }
      }

      // Set new sort (clear old sort params first)
      if (field !== 'relevance') {
        params['sort.' + field] = direction;
      }

      window.SSURLState.writeToURL(params);
      (window.__ssCollection || window.__ssSearch).refresh(params);

      document.dispatchEvent(
        new CustomEvent('ss:sort-changed', {
          detail: { field, direction },
        })
      );
    }
  }

  window.SSSorting = SSSorting;
})();
